import { FC, useState } from 'react';
import isEqual from 'react-fast-compare';
import Header from '../../../common/Header/Header';
import { showToast } from '../../../components/Toast/Toast';
import { ToastTypes } from '../../../components/Toast/Toast.types';
import { BuildRequestBodyParams } from '../../../components/TreeView/TreeView.types';
import useTreeViewRowStates from '../../../components/TreeView/useTreeViewRowStates';
import { useGetRecipeBases } from '../../../services/base/base.service';
import { useGetRecipeProducts } from '../../../services/product/product.service';
import { useAddRawProdsToRecipes } from '../../../services/recipe/recipe.service';
import { AddRawProdsToRecipesResponse, RawProdAssignedToRecipe } from '../../../services/recipe/recipe.types';
import { useGetRecipeSizes } from '../../../services/size/size.service';
import { fetchSpecsInRecipe, useSpecsInRecipe } from '../../../services/spec/spec.service';
import {
  genericErrorShowToastOptions,
  useGenericErrorMessage,
} from '../../../utils/useGenericErrorMessage/useGenericErrorMessage';
import { AddRawProdsStep } from './AddRawProds.types';
import Confirmation from './Confirmation/Confirmation';
import ReviewChanges from './ReviewChanges/ReviewChanges';
import SelectRawProds from './SelectRawProds/SelectRawProds';
import SelectRecipes from './SelectRecipes/SelectRecipes';

const AddRawProds: FC = () => {
  const [step, setStep] = useState<AddRawProdsStep>(0);
  const [selectedRawProds, setSelectedRawProds] = useState<RawProdAssignedToRecipe[]>([]);
  const [leftRowData, setLeftRowData] = useState<any[]>([]);
  const [centerRowData, setCenterRowData] = useState<any[]>([]);
  const [rightRowData, setRightRowData] = useState<any[]>([]);
  const [selectedRecipes, setSelectedRecipes] = useState<any[]>([]);
  const [response, setResponse] = useState<AddRawProdsToRecipesResponse[] | undefined>(undefined);
  const { mutate, isError, isLoading, error } = useAddRawProdsToRecipes();
  useGenericErrorMessage([{ isError, error }]);

  const handleSave = () => {
    mutate(
      {
        rawProdUsages: selectedRawProds.map((rawProd) => {
          return {
            moaRawProdNo: rawProd.moaRawProdNo,
            moaRawProdDesc: rawProd.moaRawProdDesc,
            usageAmount: rawProd.usageAmount,
          };
        }),
        specNos: selectedRecipes.map((recipe) => recipe.specNo),
      },
      {
        onSuccess: (response) => {
          setResponse(response.data);
          setStep(3);
          showToast({ label: 'Recipes have been updated.', id: 'addRawProdsToRecipesSuccess' });
        },
      }
    );
  };

  const dataStructure = [
    {
      fetchChildren: useGetRecipeBases,
      idKey: 'classNo',
      showCheckboxes: true,
      buildLabel: (params: BuildRequestBodyParams) => params.row.description,
      buildRequestBody: (params: BuildRequestBodyParams) => {
        return { classNo: params.row.classNo };
      },
    },
    {
      fetchChildren: useGetRecipeSizes,
      idKey: 'baseNo',
      showCheckboxes: true,
      buildLabel: (params: BuildRequestBodyParams) => params.row.description,
      buildRequestBody: (params: BuildRequestBodyParams) => {
        return { classNo: params.parentId, baseNo: params.row.baseNo };
      },
    },
    {
      fetchChildren: useGetRecipeProducts,
      idKey: 'sizeNo',
      buildLabel: (params: BuildRequestBodyParams) => params.row.description,
      showCheckboxes: true,
      buildRequestBody: (params: BuildRequestBodyParams) => {
        return {
          classNo: params.parentRows?.[0].id,
          baseNo: params.parentId,
          sizeNo: params.row.sizeNo,
        };
      },
    },
    {
      fetchChildren: useSpecsInRecipe,
      idKey: 'productNo',
      showCheckboxes: true,
      buildLabel: (params: BuildRequestBodyParams) => params.row.description,
      buildRequestBody: (params: BuildRequestBodyParams) => {
        return {
          classNo: params.parentRows?.[0].id,
          baseNo: params.parentRows?.[1].id,
          sizeNo: params.parentId,
          productNo: params.row.productNo,
        };
      },
    },
    {
      to: () => '',
      idKey: 'specNo',
      showCheckboxes: true,
      buildLabel: (params: BuildRequestBodyParams) => `${params.row.specNo} - ${params.row.description}`,
    },
  ];

  const { checkedRows, expandedRows, handleRowClick, handleCheckboxClick, handleUpwardsDeselectPropagation } = useTreeViewRowStates(
    dataStructure,
    'selectRecipesToAddRawProdsTo',
    true
  );

  const mapParentRowsToFilters = (parentRows: { id: string; level: number }[]) => {
    return {
      classNo: parentRows[0]?.id,
      baseNo: parentRows[1]?.id,
      sizeNo: parentRows[2]?.id,
      productNo: parentRows[3]?.id,
    };
  };

  const handleCheckboxClickInternal = (row: any) => {
    if (row.rowChecked) {
      if (row.level === 4) {
        // recipe row was checked
        setSelectedRecipes((selectedRecipes) => {
          const selectedRecipesWithCheckedRowAdded = [...selectedRecipes, row];
          const onlyUniqueValues = selectedRecipesWithCheckedRowAdded.filter((spec, index) => {
            const firstIndex = selectedRecipesWithCheckedRowAdded.findIndex((val) => val.specNo === spec.specNo);
            return index === firstIndex;
          });
          const newSelectedRecipes = onlyUniqueValues.map((recipe) => {
            return {
              ...recipe,
              rowChecked: true,
              level: 4,
              parentRows: [
                { level: 0, id: recipe.moaClassNo },
                { level: 1, id: recipe.moaBaseNo },
                { level: 2, id: recipe.moaSizeNo },
                { level: 3, id: recipe.moaProductNo },
                { level: 4, id: recipe.specNo },
              ],
            };
          });
          return newSelectedRecipes;
        });
      } else {
        // class / base / size / prod row was checked
        const filters = mapParentRowsToFilters(row.parentRows);
        let parentIndex = -1;
        if (row.level !== 0) {
          const { idKey: parentKey } = dataStructure[row.level - 1] || { idKey: false };

          parentIndex = checkedRows.findIndex(checkedRow => {
            let areParentsEquals = checkedRow.parentRows.every((element: any,  index: number) => {
              return element.id === row.parentRows[index]?.id;
            });
            return checkedRow[parentKey] === row.parentRows[checkedRow.level]?.id && areParentsEquals;
          });
        }

        // fetch the specs that correspond with that row and add them to selectedRecipes (if not already there)
        if (parentIndex === -1) {
          fetchSpecsInRecipe(
            filters,
            (response) => {
              setSelectedRecipes((selectedRecipes) => {
                const selectedRecipesWithResponseDataAdded = [...selectedRecipes, ...response.data];
                const onlyUniqueValues = selectedRecipesWithResponseDataAdded.filter((spec, index) => {
                  const firstIndex = selectedRecipesWithResponseDataAdded.findIndex((val) => val.specNo === spec.specNo);
                  return index === firstIndex;
                });
                const newSelectedRecipes = onlyUniqueValues.map((recipe) => {
                  return {
                    ...recipe,
                    rowChecked: true,
                    level: 4,
                    parentRows: [
                      { level: 0, id: recipe.moaClassNo },
                      { level: 1, id: recipe.moaBaseNo },
                      { level: 2, id: recipe.moaSizeNo },
                      { level: 3, id: recipe.moaProductNo },
                      { level: 4, id: recipe.specNo },
                    ],
                  };
                });
                return newSelectedRecipes;
              });
            },
            (error) => {
              showToast({
                ...genericErrorShowToastOptions,
                label: 'Error fetching specs',
                toastType: ToastTypes.Error,
                id: 'errorFetchingSpecsInRecipe',
              });
            }
          );
        }
      }
    } else {
      // row was just unchecked
      setSelectedRecipes((selectedRecipes) => {
        // filter through selectedRecipes and remove all the recipes that correspond with that row
        return selectedRecipes.filter((recipe) => {
          if (
            !isEqual(
              row.parentRows,
              recipe.parentRows.filter((parentRow: any) => parentRow.level <= row.level)
            )
          ) {
            return true;
          } else {
            return false;
          }
        });
      });
      handleUpwardsDeselectPropagation(row);
    }
    // call the fn from useTreeViewRowStates to mark the row as checked/unchecked there
    handleCheckboxClick(row);
  };

  const renderStep = () => {
    switch (step) {
      default:
        return (
          <SelectRawProds
            leftRowData={leftRowData}
            centerRowData={centerRowData}
            rightRowData={rightRowData}
            setLeftRowData={setLeftRowData}
            setRightRowData={setRightRowData}
            setCenterRowData={setCenterRowData}
            selectedRawProds={selectedRawProds}
            setSelectedRawProds={setSelectedRawProds}
            setStep={setStep}
          />
        );
      case 1:
        return (
          <SelectRecipes
            selectedRecipes={selectedRecipes}
            setStep={setStep}
            checkedRows={checkedRows}
            expandedRows={expandedRows}
            handleRowClick={handleRowClick}
            handleCheckboxClick={handleCheckboxClickInternal}
            dataStructure={dataStructure}
            setSelectedRecipes={setSelectedRecipes}
          />
        );
      case 2:
        return (
          <ReviewChanges
            selectedRawProds={selectedRawProds}
            selectedRecipes={selectedRecipes}
            setStep={setStep}
            handleSave={handleSave}
            isLoading={isLoading}
          />
        );
      case 3:
        return <Confirmation rowData={response} />;
    }
  };

  return (
    <>
      <Header
        breadcrumb={{ label: 'Back to Recipes', to: '/recipes' }}
        title='Add Raw Product'
        stepTracker={
          step !== 3
            ? {
                currentStep: step,
                steps: [
                  { label: 'Select Raw Products', onClick: () => setStep(0) },
                  { label: 'Recipes To Update', onClick: () => setStep(1) },
                  { label: 'Review' },
                ],
              }
            : undefined
        }
      />
      {renderStep()}
    </>
  );
};

export default AddRawProds;
